import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { candidateApi } from "@/lib/candidateApi";
import { HomeShell } from "@/shell/HomeShell";
import { Spinner } from "@/components/primitives";
import type { HomeData } from "@/lib/types";
import { companyInitials, formatDate } from "@/lib/format";
import { initialsFromName } from "@/lib/initials";

export function Briefs() {
  const { token = "" } = useParams<{ token: string }>();
  const [data, setData] = useState<HomeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    candidateApi.getHomeData(token).then((r) => {
      setLoading(false);
      if (r.ok) setData(r as HomeData);
      else setError((r as { error?: string }).error ?? "Could not load your briefs.");
    });
  }, [token]);

  if (loading) {
    return (
      <HomeShell activeTab="briefs">
        <div className="flex justify-center py-20">
          <Spinner size={32} />
        </div>
      </HomeShell>
    );
  }
  if (error || !data) {
    return (
      <HomeShell activeTab="briefs">
        <div className="py-20 text-center text-slate-500">{error ?? "Something went wrong."}</div>
      </HomeShell>
    );
  }

  const logoUrl = data.branding?.logoUrlLight || undefined;
  const logoFilter = data.branding?.logoFilter;

  return (
    <HomeShell
      agencyName={data.agencyName}
      logoUrl={logoUrl}
      logoFilter={logoFilter}
      candidateInitials={initialsFromName(data.displayName || data.firstName)}
      activeTab="briefs"
    >
      <div className="max-w-[640px]">
        <div className="mb-6">
          <p
            className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em]"
            style={{ color: "#7C7590" }}
          >
            Account
          </p>
          <h1 className="text-[22px] font-semibold mt-1" style={{ color: "#17131F" }}>
            Your Briefs
          </h1>
          <p className="text-[13px] mt-1" style={{ color: "#7C7590" }}>
            Every role your consultant has sent you, in one place.
          </p>
        </div>

        <div
          className="rounded-[18px] border overflow-hidden"
          style={{ background: "#FFFFFF", borderColor: "#EFEBF7" }}
        >
          {data.pastRoles.length === 0 ? (
            <p className="text-[13px] py-8 text-center italic" style={{ color: "#8C86A0" }}>
              Your roles will appear here as your consultant sends invites.
            </p>
          ) : (
            data.pastRoles.map((role, i) => (
              <Link
                key={role.token}
                to={`/invite/${role.token}/brief`}
                className="flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-paper-2"
                style={{
                  borderBottom: i < data.pastRoles.length - 1 ? "1px solid #F4F2F9" : "none",
                }}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-[12px] font-bold shrink-0"
                  style={{ background: "#EFEBFF", color: "var(--accent)" }}
                >
                  {companyInitials(role.company)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[14px] font-semibold truncate"
                      style={{ color: "#17131F" }}
                    >
                      {role.role}
                    </span>
                    {role.isCurrent && (
                      <span
                        className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] rounded px-1.5 py-0.5 shrink-0"
                        style={{ background: "#EFEBFF", color: "var(--accent)" }}
                      >
                        Current
                      </span>
                    )}
                  </div>
                  <div className="text-[12.5px]" style={{ color: "#7C7590" }}>
                    {role.company}
                  </div>
                </div>
                <span className="text-[11px] font-mono shrink-0" style={{ color: "#8C86A0" }}>
                  {formatDate(role.createdAt)}
                </span>
              </Link>
            ))
          )}
        </div>
      </div>
    </HomeShell>
  );
}
